import {
  fetchAddresses,
  saveAddress,
  updateAddress,
  deleteAddress,
  setDefaultAddress,
  selectAddress,
} from "../../../../slice/address/addressSlice";

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchCategoryDropdown } from '../../../../slice/category/categorySlice';
import Header from '../../Header';
import Footer from '../../components/Footer';
import { toast } from "react-toastify";

/* ─── icons ──────────────────────────────────────────── */
const IconPin = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
    <circle cx="12" cy="9" r="2.5"/>
  </svg>
);
const IconEdit = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const IconTrash = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/>
    <path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
  </svg>
);
const IconPlus = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M12 5v14M5 12h14"/>
  </svg>
);
const IconStar = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1.5">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);
const IconCheck = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const IconX = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const IconUser = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const EMPTY = { fullName: "", email: "", phone: "", address: "", district: "", state: "", pincode: "", notes: "", isDefault: false };

const SavedAddresses = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { dropdownCategories = [] } = useSelector((state) => state.category);
  const { user } = useSelector((s) => s.auth);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    dispatch(fetchCategoryDropdown());
  }, [dispatch]);

  const { addresses, loading, error } = useSelector((s) => s.address);
  const { token } = useSelector((s) => s.auth);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const [modal, setModal]       = useState(false);
  const [editId, setEditId]     = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [errors, setErrors]     = useState({});
  const [delConfirm, setDelConfirm] = useState(null);

  useEffect(() => {
    if (token) dispatch(fetchAddresses());
  }, [token, dispatch]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  const openAdd = () => { setEditId(null); setForm(EMPTY); setErrors({}); setModal(true); };

  const openEdit = (addr) => {
    setEditId(addr._id);
    setForm({
      fullName: addr.fullName || "",
      email: addr.email || "",
      phone: addr.phone || "",
      address: addr.address || "",
      district: addr.district || "",
      state: addr.state || "",
      pincode: addr.pincode || "",
      notes: addr.notes || "",
      isDefault: addr.isDefault || false,
    });
    setErrors({});
    setModal(true);
  };

  const closeModal = () => { setModal(false); setEditId(null); };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
    setErrors((p) => ({ ...p, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.fullName?.trim()) e.fullName = "Required";
    if (!form.phone || !/^[6-9]\d{9}$/.test(form.phone)) e.phone = "Valid 10-digit number";
    if (!form.address?.trim()) e.address = "Required";
    if (!form.district?.trim()) e.district = "Required";
    if (!form.state?.trim()) e.state = "Required";
    if (!form.pincode || !/^\d{6}$/.test(form.pincode)) e.pincode = "6-digit PIN";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    if (editId) {
      dispatch(updateAddress({ id: editId, ...form }))
        .unwrap()
        .then(() => { toast.success("Address updated"); closeModal(); })
        .catch(() => {});
    } else {
      dispatch(saveAddress(form))
        .unwrap()
        .then(() => { toast.success("Address saved"); closeModal(); })
        .catch(() => {});
    }
  };

  const handleDelete = (id) => {
    dispatch(deleteAddress(id))
      .unwrap()
      .then(() => { toast.success("Address removed"); setDelConfirm(null); })
      .catch(() => {});
  };

  const handleSetDefault = (id) => {
    dispatch(setDefaultAddress(id))
      .unwrap()
      .then(() => toast.success("Default address updated"))
      .catch(() => {});
  };

  const navItems = [
    { label: "Profile Overview", icon: <IconUser />, path: "/profile/overview" },
    { label: "Saved Addresses",  icon: <IconPin />,  path: "/saved-addresses" },
  ];

  const S = {
    page: { background: "#f5f5f5", minHeight: "100vh", fontFamily: "'DM Sans', 'Segoe UI', sans-serif" },

    layout: {
      maxWidth: "1100px",
      margin: "0 auto",
      padding: isMobile ? "20px 16px 100px" : "40px 24px 80px",
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      gap: isMobile ? "16px" : "24px",
      alignItems: "flex-start",
    },

    sidebar: {
      width: isMobile ? "100%" : "260px",
      flexShrink: 0,
      display: "flex",
      flexDirection: "column",
      gap: "12px",
    },

    sidebarTop: {
      background: "#fff",
      borderRadius: "16px",
      padding: isMobile ? "20px 16px" : "28px 20px 24px",
      textAlign: "center",
      border: "1px solid #e5e7eb",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    },
    avatarCircle: {
      width: "64px", height: "64px", borderRadius: "50%",
      background: "#fff3ea", border: "2px solid #fed7aa",
      display: "flex", alignItems: "center", justifyContent: "center",
      margin: "0 auto 14px", color: "#f97316",
    },
    sidebarName: { fontSize: "15px", fontWeight: 700, color: "#111", marginBottom: "4px" },
    sidebarSub:  { fontSize: "12px", color: "#888" },

    sidebarNav: {
      background: "#fff",
      borderRadius: "16px",
      padding: "8px 0",
      border: "1px solid #e5e7eb",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      overflow: "hidden",
      display: isMobile ? "flex" : "block",
    },
    navItem: {
      display: "flex", alignItems: "center", gap: "12px",
      padding: "12px 20px", cursor: "pointer",
      fontSize: "14px", color: "#444", fontWeight: 500,
      transition: "background 0.15s, color 0.15s",
      flex: isMobile ? 1 : "unset",
      justifyContent: isMobile ? "center" : "flex-start",
    },
    navItemActive: {
      background: "#fff3ea", color: "#f97316", fontWeight: 600,
      borderLeft: isMobile ? "none" : "3px solid #f97316",
      borderBottom: isMobile ? "3px solid #f97316" : "none",
    },
    navItemIcon: { display: "flex", alignItems: "center", flexShrink: 0 },

    right: { flex: 1, minWidth: 0, width: isMobile ? "100%" : "auto" },

    topRow: {
      display: "flex",
      alignItems: isMobile ? "flex-start" : "center",
      justifyContent: "space-between",
      marginBottom: "24px",
      flexWrap: "wrap",
      gap: "12px",
    },
    pageTitle: { fontSize: isMobile ? "18px" : "22px", fontWeight: 700, color: "#111", margin: 0 },
    pageSubtitle: { fontSize: "13px", color: "#888", marginTop: "3px" },
    btnAdd: {
      display: "inline-flex", alignItems: "center", gap: "7px",
      background: "#f97316", color: "#fff", border: "none",
      borderRadius: "10px", padding: "10px 20px",
      fontSize: "14px", fontWeight: 600, cursor: "pointer",
      whiteSpace: "nowrap",
    },

    grid: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(320px, 1fr))",
      gap: "16px",
    },
    card: {
      background: "#ffffff", border: "1px solid #e5e7eb",
      borderRadius: "16px", padding: isMobile ? "16px" : "20px",
      position: "relative", transition: "box-shadow 0.2s, border-color 0.2s",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    },
    cardDefault: { borderColor: "#f97316", background: "#fffaf7", boxShadow: "0 1px 6px rgba(249,115,22,0.12)" },
    defaultBadge: { display: "inline-flex", alignItems: "center", gap: "5px", background: "#fff3ea", color: "#ea580c", fontSize: "11px", fontWeight: 600, padding: "4px 10px", borderRadius: "20px", marginBottom: "10px" },
    cardName: { fontSize: "15px", fontWeight: 600, color: "#111", marginBottom: "6px" },
    cardLine: { fontSize: "13px", color: "#555", lineHeight: 1.7 },
    cardMeta: { fontSize: "12px", color: "#888", marginTop: "6px" },
    divider: { height: "1px", background: "#f0f0f0", margin: "14px 0" },
    actionRow: { display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" },
    btnEdit: { display: "inline-flex", alignItems: "center", gap: "5px", background: "none", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "6px 14px", fontSize: "12px", color: "#444", cursor: "pointer", fontWeight: 500 },
    btnDel: { display: "inline-flex", alignItems: "center", gap: "5px", background: "none", border: "1px solid #fee2e2", borderRadius: "8px", padding: "6px 14px", fontSize: "12px", color: "#dc2626", cursor: "pointer", fontWeight: 500 },
    btnDefault: { marginLeft: isMobile ? "0" : "auto", display: "inline-flex", alignItems: "center", gap: "5px", background: "none", border: "1px solid #fed7aa", borderRadius: "8px", padding: "6px 14px", fontSize: "12px", color: "#f97316", cursor: "pointer", fontWeight: 500 },

    empty: { textAlign: "center", padding: "60px 20px", color: "#aaa", background: "#ffffff", borderRadius: "16px", border: "1px solid #e5e7eb" },
    emptyIcon: { width: "56px", height: "56px", borderRadius: "50%", background: "#fff3ea", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", color: "#f97316" },
    emptyTitle: { fontSize: "16px", fontWeight: 600, color: "#333", marginBottom: "6px" },
    emptyText: { fontSize: "13px", color: "#888" },
    btnEmptyAdd: { display: "inline-flex", alignItems: "center", gap: "6px", marginTop: "20px", background: "#f97316", color: "#fff", border: "none", borderRadius: "10px", padding: "10px 24px", fontSize: "14px", fontWeight: 600, cursor: "pointer" },

    overlay: { position: "fixed", marginTop: "65px", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100000, padding: "16px" },
    modal: {
      background: "#fff", borderRadius: "20px",
      width: "100%", maxWidth: "520px",
      padding: isMobile ? "20px 16px" : "28px",
      maxHeight: "90vh", overflowY: "auto",
      position: "relative", boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
    },
    modalHead: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px" },
    modalTitle: { fontSize: "18px", fontWeight: 600, color: "#111" },
    btnClose: { background: "none", border: "1px solid #e5e7eb", borderRadius: "8px", width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#666" },
    label: { display: "block", fontSize: "12px", fontWeight: 600, color: "#555", marginBottom: "6px" },
    input: { width: "100%", padding: "10px 13px", border: "1.5px solid #e5e7eb", borderRadius: "10px", fontSize: "13px", color: "#111", outline: "none", background: "#fff", fontFamily: "inherit", transition: "border-color 0.2s", boxSizing: "border-box" },
    inputErr: { borderColor: "#f87171" },
    errText: { fontSize: "11px", color: "#dc2626", marginTop: "4px" },
    checkRow: { display: "flex", alignItems: "center", gap: "9px", marginTop: "4px", marginBottom: "20px", cursor: "pointer" },
    checkLabel: { fontSize: "13px", color: "#555", fontWeight: 500 },
    modalFooter: {
      display: "flex", gap: "10px",
      justifyContent: isMobile ? "stretch" : "flex-end",
    },
    btnCancel: {
      background: "none", border: "1.5px solid #e5e7eb", borderRadius: "10px",
      padding: "10px 22px", fontSize: "13px", fontWeight: 600, color: "#555", cursor: "pointer",
      flex: isMobile ? 1 : "unset",
    },
    btnSave: {
      background: "#f97316", color: "#fff", border: "none", borderRadius: "10px",
      padding: "10px 28px", fontSize: "13px", fontWeight: 600, cursor: "pointer",
      flex: isMobile ? 1 : "unset",
    },

    delBox: { background: "#fff", borderRadius: "16px", padding: "24px", maxWidth: "340px", width: "100%", textAlign: "center", boxShadow: "0 8px 32px rgba(0,0,0,0.12)" },
    delTitle: { fontSize: "16px", fontWeight: 600, color: "#111", marginBottom: "8px" },
    delText: { fontSize: "13px", color: "#666", marginBottom: "20px" },
    delBtns: { display: "flex", gap: "10px", justifyContent: "center" },
    btnDelCancel: { background: "none", border: "1.5px solid #e5e7eb", borderRadius: "10px", padding: "9px 22px", fontSize: "13px", fontWeight: 600, color: "#444", cursor: "pointer", flex: 1 },
    btnDelConfirm: { background: "#dc2626", color: "#fff", border: "none", borderRadius: "10px", padding: "9px 22px", fontSize: "13px", fontWeight: 600, cursor: "pointer", flex: 1 },

    modalGrid: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
      gap: "14px",
    },
  };

  return (
    <div className="page-wraper">
      <Header dropdownCategories={dropdownCategories} />
      <div className="content-inner-1 bg-light">
        <div className="container heighT">
          <div className="row">
            <div style={S.layout}>

              {/* ══ LEFT SIDEBAR ══ */}
              <aside style={S.sidebar}>
                <div style={S.sidebarTop}>
                  <div style={S.avatarCircle}>
                    <IconUser />
                  </div>
                  <p style={S.sidebarName}>{user?.name || "User"}</p>
                  <p style={S.sidebarSub}>Saved Addresses</p>
                  <p style={{ ...S.sidebarSub, marginTop: "2px" }}>Manage your delivery addresses</p>
                </div>

                <nav style={S.sidebarNav}>
                  {navItems.map((item) => {
                    const isActive = item.path === "/saved-addresses";
                    return (
                      <div
                        key={item.label}
                        style={{
                          ...S.navItem,
                          ...(isActive ? S.navItemActive : {}),
                          paddingLeft: isActive && !isMobile ? "17px" : isMobile ? "12px" : "20px",
                        }}
                        onClick={() => navigate(item.path)}
                        onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "#f9fafb"; }}
                        onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                      >
                        <span style={{ ...S.navItemIcon, color: isActive ? "#f97316" : "#888" }}>
                          {item.icon}
                        </span>
                        {item.label}
                      </div>
                    );
                  })}
                </nav>
              </aside>

              {/* ══ RIGHT CONTENT ══ */}
              <div style={S.right}>
                <div style={S.topRow}>
                  <div>
                    <h1 style={S.pageTitle}>Saved Addresses</h1>
                    <p style={S.pageSubtitle}>Manage your delivery addresses</p>
                  </div>
                  {addresses.length > 0 && (
                    <button style={S.btnAdd} onClick={openAdd}
                      onMouseEnter={e => e.currentTarget.style.background = "#ea580c"}
                      onMouseLeave={e => e.currentTarget.style.background = "#f97316"}>
                      <IconPlus /> Add New Address
                    </button>
                  )}
                </div>

                {loading && (
                  <div style={{ textAlign: "center", padding: "60px 0", color: "#aaa", fontSize: "14px" }}>
                    Loading addresses…
                  </div>
                )}

                {!loading && addresses.length === 0 && (
                  <div style={S.empty}>
                    <div style={S.emptyIcon}><IconPin /></div>
                    <p style={S.emptyTitle}>No addresses saved</p>
                    <p style={S.emptyText}>Add your first delivery address to get started.</p>
                    <button style={S.btnEmptyAdd} onClick={openAdd}>
                      <IconPlus /> Add Address
                    </button>
                  </div>
                )}

                {!loading && addresses.length > 0 && (
                  <div style={S.grid}>
                    {addresses.map((addr) => (
                      <div
                        key={addr._id}
                        style={{ ...S.card, ...(addr.isDefault ? S.cardDefault : {}) }}
                        onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.10)"; }}
                        onMouseLeave={e => e.currentTarget.style.boxShadow = addr.isDefault ? "0 1px 6px rgba(249,115,22,0.12)" : "0 1px 3px rgba(0,0,0,0.05)"}
                      >
                        {addr.isDefault && (
                          <div style={S.defaultBadge}>
                            <IconStar /> Default Address
                          </div>
                        )}
                        <p style={S.cardName}>{addr.fullName}</p>
                        <p style={S.cardLine}>
                          {addr.address},<br />
                          {addr.district}, {addr.state} – {addr.pincode}
                        </p>
                        <p style={S.cardMeta}>{addr.email} &nbsp;·&nbsp; {addr.phone}</p>
                        {addr.notes && <p style={{ ...S.cardMeta, fontStyle: "italic" }}>"{addr.notes}"</p>}
                        <div style={S.divider} />
                        <div style={S.actionRow}>
                          <button style={S.btnEdit} onClick={() => openEdit(addr)}>
                            <IconEdit /> Edit
                          </button>
                          <button style={S.btnDel} onClick={() => setDelConfirm(addr._id)}>
                            <IconTrash /> Remove
                          </button>
                          {!addr.isDefault && (
                            <button style={S.btnDefault} onClick={() => handleSetDefault(addr._id)}>
                              <IconCheck /> Set default
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* ── Add / Edit Modal ── */}
            {modal && (
              <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && closeModal()}>
                <div style={S.modal}>
                  <div style={S.modalHead}>
                    <h2 style={S.modalTitle}>{editId ? "Edit Address" : "Add New Address"}</h2>
                    <button style={S.btnClose} onClick={closeModal}><IconX /></button>
                  </div>

                  <div style={S.modalGrid}>
                    <div>
                      <label style={S.label}>Full Name <span style={{ color: "#f97316" }}>*</span></label>
                      <input name="fullName" placeholder="Rahul Kumar" value={form.fullName} onChange={handleChange}
                        style={{ ...S.input, ...(errors.fullName ? S.inputErr : {}) }} />
                      {errors.fullName && <p style={S.errText}>{errors.fullName}</p>}
                    </div>
                    <div>
                      <label style={S.label}>Phone Number <span style={{ color: "#f97316" }}>*</span></label>
                      <input name="phone" placeholder="9876543210" value={form.phone} onChange={handleChange} type="tel"
                        style={{ ...S.input, ...(errors.phone ? S.inputErr : {}) }} />
                      {errors.phone && <p style={S.errText}>{errors.phone}</p>}
                    </div>
                    <div style={{ gridColumn: isMobile ? "auto" : "1 / -1" }}>
                      <label style={S.label}>Email Address <span style={{ color: "#f97316" }}>(Optional)</span></label>
                      <input type="email" name="email" placeholder="you@example.com" value={form.email} onChange={handleChange}
                        style={{ ...S.input, ...(errors.email ? S.inputErr : {}) }} />
                      {errors.email && <p style={S.errText}>{errors.email}</p>}
                    </div>
                    <div style={{ gridColumn: isMobile ? "auto" : "1 / -1" }}>
                      <label style={S.label}>Street Address <span style={{ color: "#f97316" }}>*</span></label>
                      <textarea name="address" placeholder="House no, Street, Area" value={form.address} onChange={handleChange} rows={2}
                        style={{ ...S.input, ...(errors.address ? S.inputErr : {}), resize: "vertical" }} />
                      {errors.address && <p style={S.errText}>{errors.address}</p>}
                    </div>
                    <div>
                      <label style={S.label}>District <span style={{ color: "#f97316" }}>*</span></label>
                      <input name="district" placeholder="Chennai" value={form.district} onChange={handleChange}
                        style={{ ...S.input, ...(errors.district ? S.inputErr : {}) }} />
                      {errors.district && <p style={S.errText}>{errors.district}</p>}
                    </div>
                    <div>
                      <label style={S.label}>State <span style={{ color: "#f97316" }}>*</span></label>
                      <input name="state" placeholder="Tamil Nadu" value={form.state} onChange={handleChange}
                        style={{ ...S.input, ...(errors.state ? S.inputErr : {}) }} />
                      {errors.state && <p style={S.errText}>{errors.state}</p>}
                    </div>
                    <div>
                      <label style={S.label}>PIN Code <span style={{ color: "#f97316" }}>*</span></label>
                      <input name="pincode" placeholder="600001" value={form.pincode} onChange={handleChange}
                        style={{ ...S.input, ...(errors.pincode ? S.inputErr : {}) }} />
                      {errors.pincode && <p style={S.errText}>{errors.pincode}</p>}
                    </div>
                    <div>
                      <label style={S.label}>Notes (Optional)</label>
                      <input name="notes" placeholder="Landmark, instructions…" value={form.notes} onChange={handleChange}
                        style={S.input} />
                    </div>
                  </div>

                  <label style={S.checkRow}>
                    <input type="checkbox" name="isDefault" checked={form.isDefault} onChange={handleChange}
                      style={{ accentColor: "#f97316", width: "16px", height: "16px" }} />
                    <span style={S.checkLabel}>Set as default delivery address</span>
                  </label>

                  <div style={S.modalFooter}>
                    <button style={S.btnCancel} onClick={closeModal}>Cancel</button>
                    <button style={S.btnSave} onClick={handleSubmit} disabled={loading}>
                      {loading ? "Saving…" : editId ? "Update Address" : "Save Address"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Delete Confirm ── */}
            {delConfirm && (
              <div style={S.overlay}>
                <div style={S.delBox}>
                  <div style={{ ...S.emptyIcon, background: "#fee2e2", color: "#dc2626", marginBottom: "14px" }}>
                    <IconTrash />
                  </div>
                  <p style={S.delTitle}>Remove Address?</p>
                  <p style={S.delText}>This address will be permanently deleted from your account.</p>
                  <div style={S.delBtns}>
                    <button style={S.btnDelCancel} onClick={() => setDelConfirm(null)}>Cancel</button>
                    <button style={S.btnDelConfirm} onClick={() => handleDelete(delConfirm)}>
                      {loading ? "Removing…" : "Yes, Remove"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SavedAddresses;